<script setup lang="ts">
import { Check, Copy, Mail, Phone } from "lucide-vue-next";
import type { PublicWorkerProfile } from "~/types/public-worker";

const props = defineProps<{ worker: PublicWorkerProfile }>();
const { t } = useI18n();
const toast = useToast();
const copied = ref<"phone" | "email" | null>(null);
const fullName = computed(() =>
  [props.worker.name, props.worker.lastname]
    .filter(Boolean)
    .join(" ")
    .trim()
    .replace(/(^|[\s'-])\p{L}/gu, value => value.toUpperCase()),
);

async function copyValue(type: "phone" | "email", value: string) {
  try {
    await navigator.clipboard.writeText(value);
    copied.value = type;
    toast.success({
      title: t("worker_profile.contact_copied_title"),
      description: t("worker_profile.contact_copied_description"),
    });
    window.setTimeout(() => {
      if (copied.value === type) copied.value = null;
    }, 1600);
  } catch {
    toast.error({
      title: t("worker_profile.copy_error_title"),
      description: t("worker_profile.copy_error_description"),
    });
  }
}
</script>

<template>
  <section
    v-if="worker.contactPhone || worker.contactEmail"
    id="contacts"
    class="scroll-mt-36 border-b border-app-border py-10 sm:py-12"
  >
    <WorkerPublicSectionHeading
      :title="$t('worker_profile.contacts')"
      :description="
        $t('worker_profile.contacts_description_name', { name: fullName })
      "
    />

    <div class="mt-6 grid gap-4 sm:grid-cols-2">
      <div
        v-if="worker.contactPhone"
        class="flex min-w-0 items-center gap-3 rounded-xl border border-app-border bg-white p-4"
      >
        <span
          class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary"
        >
          <Phone class="size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-app-muted">
            {{ $t("worker_profile.phone_label") }}
          </p>
          <a
            :href="`tel:${worker.contactPhone}`"
            class="mt-1 block truncate text-base font-semibold text-app-text hover:text-primary"
          >
            {{ worker.contactPhone }}
          </a>
        </div>
        <button
          type="button"
          class="flex size-9 shrink-0 items-center justify-center rounded-lg text-app-muted transition hover:bg-primary-50 hover:text-primary"
          :aria-label="$t('worker_profile.copy_phone')"
          @click="copyValue('phone', worker.contactPhone)"
        >
          <Check v-if="copied === 'phone'" class="size-4 text-primary" />
          <Copy v-else class="size-4" />
        </button>
      </div>

      <div
        v-if="worker.contactEmail"
        class="flex min-w-0 items-center gap-3 rounded-xl border border-app-border bg-white p-4"
      >
        <span
          class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary"
        >
          <Mail class="size-5" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-app-muted">
            {{ $t("worker_profile.email_label") }}
          </p>
          <a
            :href="`mailto:${worker.contactEmail}`"
            class="mt-1 block truncate text-base font-semibold text-app-text hover:text-primary"
          >
            {{ worker.contactEmail }}
          </a>
        </div>
        <button
          type="button"
          class="flex size-9 shrink-0 items-center justify-center rounded-lg text-app-muted transition hover:bg-primary-50 hover:text-primary"
          :aria-label="$t('worker_profile.copy_email')"
          @click="copyValue('email', worker.contactEmail)"
        >
          <Check v-if="copied === 'email'" class="size-4 text-primary" />
          <Copy v-else class="size-4" />
        </button>
      </div>
    </div>
  </section>
</template>
